import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import CustomToast from "src/components/CustomToast/CustomToast";
import cmsClient from "src/services/cmsClient";

const togglePublished = async ({
	id,
	isPublished
}: {
	id: number;
	isPublished: boolean;
}) => {
	const response = await cmsClient.patch<IContentOrder>(
		`/homepage/admin/content-order/${id}/`,
		{ is_published: isPublished }
	);

	return response.data;
};

const useToggleContentOrderPublished = () => {
	const queryClient = useQueryClient();

	const onMutate = async (variables: { id: number; isPublished: boolean }) => {
		await queryClient.cancelQueries(["content-orders"]);

		const previous = queryClient.getQueryData<IContentOrder[]>([
			"content-orders"
		]);

		queryClient.setQueryData<IContentOrder[]>(["content-orders"], (old) =>
			(old ?? []).map((order) =>
				order.id === variables.id
					? { ...order, is_published: variables.isPublished }
					: order
			)
		);

		return { previous };
	};

	const onSuccess = (
		_data: IContentOrder,
		variables: { isPublished: boolean }
	) => {
		const message = variables.isPublished
			? "Section is now live"
			: "Section moved to drafts";

		toast.success(<CustomToast type="Success" message={message} />, {
			hideProgressBar: true
		});
	};

	const onSettled = () => {
		queryClient.invalidateQueries(["content-orders"]);
	};

	const onError = (
		_error: unknown,
		_variables: { id: number; isPublished: boolean },
		context?: { previous?: IContentOrder[] }
	) => {
		if (context?.previous) {
			queryClient.setQueryData(["content-orders"], context.previous);
		}

		toast.error(
			<CustomToast type="Error" message="Something went wrong, try again" />,
			{ hideProgressBar: true }
		);
	};

	const mutation = useMutation(togglePublished, {
		onMutate,
		onError,
		onSuccess,
		onSettled
	});

	return mutation;
};

export default useToggleContentOrderPublished;
